import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Mail,
  MapPin,
  Briefcase,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  FileText,
  Calendar,
} from "lucide-react";
import {
  useGetAllApplications,
  Application,
  ApplicationStatus,
} from "../../lib/api/adminApplications";
import ApplicationDetailsSidebar from "../../components/admin/applications/ApplicationDetailsSidebar";

const AdminApplications: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [jobFilter, setJobFilter] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data, isLoading } = useGetAllApplications({
    page,
    limit: 10,
    search: debouncedSearch,
    status: statusFilter || undefined,
    jobId: jobFilter || undefined,
  });

  console.log("data");
  console.log(data);

  const applications = data || [];
  //   const total = data?.total || 0;
  //   const currentPage = data?.currentPage || 1;
  //   const totalPages = data?.totalPages || 1;

  //   const perPage =
  //     totalPages > 0 ? Math.ceil(total / totalPages) : applications.length;
  //   const startIndex = total > 0 ? (currentPage - 1) * perPage + 1 : 0;
  //   const endIndex =
  //     total > 0 ? Math.min(startIndex + applications.length - 1, total) : 0;

  const handleRowClick = (application: Application) => {
    setSelectedApplication(application);
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setTimeout(() => setSelectedApplication(null), 300);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusConfig = (status: ApplicationStatus) => {
    const configs = {
      pending: {
        bg: "bg-amber-50",
        text: "text-amber-700",
        icon: Clock,
      },
      reviewed: {
        bg: "bg-blue-50",
        text: "text-blue-700",
        icon: AlertCircle,
      },
      shortlisted: {
        bg: "bg-purple-50",
        text: "text-purple-700",
        icon: CheckCircle,
      },
      rejected: {
        bg: "bg-red-50",
        text: "text-red-700",
        icon: XCircle,
      },
      hired: {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        icon: CheckCircle,
      },
    };
    return configs[status] || configs.pending;
  };

  const getExperienceLabel = (exp: string) => {
    const labels: Record<string, string> = {
      entry: "Entry",
      "1-2": "1-2 yrs",
      "3-5": "3-5 yrs",
      "5+": "5+ yrs",
    };
    return labels[exp] || exp;
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setJobFilter("");
    setPage(1);
  };

  const hasActiveFilters = searchQuery || statusFilter || jobFilter;

  // Get unique job titles for filter
  const uniqueJobTitles = Array.from(
    new Set(applications.map((a) => a.jobTitle))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
          Applications
        </h1>
        <p className="text-gray-500 mt-1">Review and manage job applications</p>
      </div>

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: "Total",
            value: total,
            color: "bg-gray-100 text-gray-700",
          },
          {
            label: "Pending",
            value: applications.filter((a) => a.status === "pending").length,
            color: "bg-amber-50 text-amber-700",
          },
          {
            label: "Shortlisted",
            value: applications.filter((a) => a.status === "shortlisted")
              .length,
            color: "bg-purple-50 text-purple-700",
          },
          {
            label: "Hired",
            value: applications.filter((a) => a.status === "hired").length,
            color: "bg-emerald-50 text-emerald-700",
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className={`${stat.color} rounded-xl p-4 text-center`}
          >
            <p className="text-2xl font-bold">{stat.value}</p>
            <p className="text-sm opacity-80">{stat.label}</p>
          </div>
        ))}
      </div> */}

      {/* Main Content */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Search and Filters */}
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                showFilters || hasActiveFilters
                  ? "border-[#8B52FF] bg-[#8B52FF]/5 text-[#8B52FF]"
                  : "border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-[#8B52FF]" />
              )}
            </button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-end gap-4">
              <div className="min-w-[150px]">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] bg-white"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="rejected">Rejected</option>
                  <option value="hired">Hired</option>
                </select>
              </div>

              <div className="min-w-[180px]">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Job Position
                </label>
                <select
                  value={jobFilter}
                  onChange={(e) => {
                    setJobFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] bg-white"
                >
                  <option value="">All Positions</option>
                  {uniqueJobTitles.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>

        {/* Applications Table */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#8B52FF]" />
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No applications found
              </h3>
              <p className="text-gray-500">
                {hasActiveFilters
                  ? "Try adjusting your filters"
                  : "No applications submitted yet"}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Location
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Experience
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Applied
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {applications.map((application) => {
                  const statusConfig = getStatusConfig(application.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <tr
                      key={application._id}
                      onClick={() => handleRowClick(application)}
                      className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                            {application.firstName[0]}
                            {application.lastName[0]}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {application.firstName} {application.lastName}
                            </p>
                            <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {application.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate max-w-[150px]">
                            {application.jobTitle}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-600 truncate max-w-[120px]">
                            {application.location}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden lg:table-cell">
                        <span className="text-sm text-gray-600">
                          {getExperienceLabel(application.experience)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
                        >
                          <StatusIcon className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">
                            {application.status.charAt(0).toUpperCase() +
                              application.status.slice(1)}
                          </span>
                        </span>
                      </td>
                      <td className="py-4 px-4 hidden sm:table-cell">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(application.createdAt)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {/* {totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              Showing {startIndex} to {endIndex} of {total} applications
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )} */}
      </div>

      {/* Application Details Sidebar */}
      <ApplicationDetailsSidebar
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
        application={selectedApplication}
      />
    </div>
  );
};

export default AdminApplications;
